import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { colorOf, fmtDate, EVENT_TYPES } from "@/lib/constants";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Loader2, CalendarPlus, Download } from "lucide-react";
import { googleCalUrl, downloadICS } from "@/lib/calendar";
import { toast } from "sonner";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      const [c, e] = await Promise.all([api.get("/courses"), api.get("/events")]);
      setCourses(c.data); setEvents(e.data); setLoading(false);
    })();
  }, []);

  const courseById = Object.fromEntries(courses.map((c) => [c.course_id, c]));
  const labelFor = (e) => { const c = courseById[e.course_id]; return c?.code || c?.name || ""; };
  const exportAll = () => {
    const dated = events.filter((e) => e.date);
    if (dated.length === 0) { toast.error("No dated events to export yet."); return; }
    downloadICS(dated, labelFor);
    toast.success(`Added ${dated.length} deadlines to your calendar file. Open it to import into Google Calendar.`);
  };
  const eventsByDate = {};
  events.forEach((e) => { if (e.date) { (eventsByDate[e.date] ||= []).push(e); } });

  const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  const monthLabel = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const renderMonth = () => {
    const year = cursor.getFullYear(), month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startDay = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    const todayKey = dateKey(new Date());
    return (
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50">
          {WEEKDAYS.map((w) => <div key={w} className="px-2 py-2 text-center text-xs font-semibold text-slate-400">{w}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const key = d ? dateKey(d) : `empty-${i}`;
            const dayEvents = d ? (eventsByDate[key] || []) : [];
            return (
              <div key={key} className={`min-h-[90px] sm:min-h-[110px] border-b border-r border-slate-100 p-1.5 ${d && key === todayKey ? "bg-blue-50/40" : ""}`}>
                {d && <div className={`text-xs mb-1 ${key === todayKey ? "font-bold text-blue-600" : "text-slate-400"}`}>{d.getDate()}</div>}
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((e) => {
                    const col = colorOf(courseById[e.course_id]?.color);
                    return (
                      <button key={e.event_id} onClick={() => setSelected(e)} data-testid={`cal-event-${e.event_id}`}
                        className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[10px] font-medium ${col.bg} ${col.text}`}>
                        {e.title}
                      </button>
                    );
                  })}
                  {dayEvents.length > 3 && <span className="text-[10px] text-slate-400 px-1">+{dayEvents.length - 3} more</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderList = () => {
    const sorted = [...events].filter((e) => e.date).sort((a, b) => new Date(a.date) - new Date(b.date));
    return (
      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
        {sorted.length === 0 && <div className="p-8 text-center text-slate-500">No dated events yet.</div>}
        {sorted.map((e) => {
          const c = courseById[e.course_id]; const col = colorOf(c?.color);
          return (
            <button key={e.event_id} onClick={() => setSelected(e)} data-testid={`cal-list-${e.event_id}`}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50">
              <span className={`h-2.5 w-2.5 rounded-full ${col.dot}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{e.title}</p>
                <p className="text-xs text-slate-500">{c?.code || c?.name} · {e.type}</p>
              </div>
              <span className="text-sm text-slate-500 shrink-0">{fmtDate(e.date)}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderWeek = () => {
    const start = new Date(cursor);
    start.setDate(start.getDate() - start.getDay());
    const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
    return (
      <div className="grid grid-cols-1 sm:grid-cols-7 gap-3">
        {days.map((d) => {
          const key = dateKey(d);
          const dayEvents = eventsByDate[key] || [];
          return (
            <div key={key} className="rounded-xl border border-slate-200 bg-white p-3 min-h-[120px]">
              <p className="text-xs font-semibold text-slate-400 mb-2">{WEEKDAYS[d.getDay()]} {d.getDate()}</p>
              <div className="space-y-1.5">
                {dayEvents.map((e) => {
                  const col = colorOf(courseById[e.course_id]?.color);
                  return (
                    <button key={e.event_id} onClick={() => setSelected(e)} data-testid={`cal-week-${e.event_id}`}
                      className={`block w-full truncate rounded px-1.5 py-1 text-left text-[11px] font-medium ${col.bg} ${col.text}`}>
                      {e.title}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const move = (dir) => {
    const c = new Date(cursor);
    if (view === "month") c.setMonth(c.getMonth() + dir);
    else if (view === "week") c.setDate(c.getDate() + dir * 7);
    setCursor(c);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Calendar</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportAll} data-testid="export-ics-btn" className="rounded-lg">
            <Download className="h-4 w-4 mr-1" /> Add all to calendar
          </Button>
          <Tabs value={view} onValueChange={setView}>
            <TabsList data-testid="calendar-view-tabs">
              <TabsTrigger value="month" data-testid="cal-month-btn">Month</TabsTrigger>
              <TabsTrigger value="week" data-testid="cal-week-btn">Week</TabsTrigger>
              <TabsTrigger value="list" data-testid="cal-list-btn">List</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {view !== "list" && (
        <div className="flex items-center justify-between">
          <button onClick={() => move(-1)} data-testid="cal-prev" className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><ChevronLeft className="h-5 w-5" /></button>
          <span className="font-medium text-slate-900">{view === "month" ? monthLabel : cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
          <button onClick={() => move(1)} data-testid="cal-next" className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><ChevronRight className="h-5 w-5" /></button>
        </div>
      )}

      {view === "month" && renderMonth()}
      {view === "week" && renderWeek()}
      {view === "list" && renderList()}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="event-detail-modal">
          {selected && (
            <>
              <DialogHeader><DialogTitle>{selected.title}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                <p><span className="text-slate-400">Course:</span> {courseById[selected.course_id]?.name || "—"}</p>
                <p><span className="text-slate-400">Type:</span> <span className="capitalize">{selected.type}</span></p>
                <p><span className="text-slate-400">Date:</span> {fmtDate(selected.date)}</p>
                {selected.weight != null && <p><span className="text-slate-400">Weight:</span> {selected.weight}%</p>}
                {selected.description && <p className="text-slate-600">{selected.description}</p>}
                {selected.needs_review && <p className="text-amber-600 font-medium">Needs review</p>}
              </div>
              {selected.date && (
                <a href={googleCalUrl(selected, labelFor(selected))} target="_blank" rel="noopener noreferrer"
                  data-testid="add-to-gcal-btn"
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2.5 transition-colors">
                  <CalendarPlus className="h-4 w-4" /> Add to Google Calendar
                </a>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
